import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, Wallet, Upload, Trash2, Image as ImageIcon,
  CreditCard, KeyRound, RotateCcw, PlugZap, Eye, EyeOff,
  Link2, CheckCircle2, ExternalLink, ChevronDown, Info, Copy,
  MessageCircle, LifeBuoy,
} from "lucide-react";

export default function StoreSettings() {
  const [s, setS] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef();

  // OAuth state
  const [oauth, setOauth] = useState(null); // {oauth_available, connected, connection, ...}
  const [connecting, setConnecting] = useState(false);
  const [showPartnerHelp, setShowPartnerHelp] = useState(false);

  // Manual keys (legacy — collapsed)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pay, setPay] = useState(null);
  const [payDraft, setPayDraft] = useState({ key_id: "", key_secret: "", mode: "test" });
  const [showSecret, setShowSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingPay, setSavingPay] = useState(false);

  // Support (WhatsApp)
  const [support, setSupport] = useState(null); // {available, wa_link, whatsapp_number, prefilled_text}

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/store/me").then((r) => setS(r.data));
    loadOauth();
    loadPay();
    api.get("/store/support/whatsapp").then((r) => setSupport(r.data)).catch(() => setSupport({ available: false }));
  }, []);

  // Handle callback query params (?razorpay=connected | ?razorpay_error=...)
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const ok = q.get("razorpay");
    const err = q.get("razorpay_error");
    if (ok === "connected") {
      toast.success("Razorpay account connected successfully");
      loadOauth();
      navigate("/store/settings", { replace: true });
    } else if (err) {
      toast.error(`Razorpay connect failed: ${err.replace(/_/g, " ")}`);
      navigate("/store/settings", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loadOauth = () => api.get("/store/payments/razorpay/status").then((r) => setOauth(r.data)).catch(() => {});
  const loadPay = () => api.get("/store/payment-settings").then((r) => setPay(r.data)).catch(() => {});

  const save = async () => {
    try {
      await api.patch("/store/settings", {
        name: s.name, address: s.address, city: s.city, state: s.state, pincode: s.pincode, mobile: s.mobile,
        require_approval: !!s.require_approval,
        accept_cash: !!s.accept_cash,
        accept_online: !!s.accept_online,
        auto_purge_days: Number(s.auto_purge_days || 0),
      });
      toast.success("Saved");
    } catch (e) { toast.error(formatError(e)); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/store/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, logo_url: data.logo_url });
      toast.success("Logo updated");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploadingLogo(false); }
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove the logo?")) return;
    await api.delete("/store/logo");
    setS({ ...s, logo_url: "" });
    toast.success("Logo removed");
  };

  const connectRazorpay = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/store/payments/razorpay/connect-url");
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error(formatError(e));
      setConnecting(false);
    }
  };
  const disconnectRazorpay = async () => {
    if (!window.confirm("Disconnect your Razorpay account? Customer payments will fall back to the platform default until you reconnect.")) return;
    try {
      await api.delete("/store/payments/razorpay/disconnect");
      toast.success("Razorpay disconnected");
      await loadOauth();
    } catch (e) { toast.error(formatError(e)); }
  };

  const testPay = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/store/payment-settings/test", {
        key_id: payDraft.key_id || undefined,
        key_secret: payDraft.key_secret || undefined,
      });
      toast.success(`Razorpay ${data.mode || ""} keys verified`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setTesting(false); }
  };
  const savePay = async () => {
    if (!payDraft.key_id.trim() || !payDraft.key_secret.trim()) {
      return toast.error("Enter both Key ID and Key Secret");
    }
    setSavingPay(true);
    try {
      await api.patch("/store/payment-settings", payDraft);
      toast.success("Razorpay keys saved & verified");
      setPayDraft({ key_id: "", key_secret: "", mode: payDraft.mode });
      setShowSecret(false);
      await loadPay();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingPay(false); }
  };
  const resetPay = async () => {
    if (!window.confirm("Reset to the platform default keys? Your custom Razorpay credentials will be removed.")) return;
    try {
      await api.delete("/store/payment-settings");
      toast.success("Reverted to platform default");
      await loadPay();
    } catch (e) { toast.error(formatError(e)); }
  };

  const copyRedirectUri = () => {
    const uri = oauth?.redirect_uri || "";
    if (!uri) return;
    navigator.clipboard.writeText(uri);
    toast.success("Callback URL copied");
  };

  if (!s) return null;

  const conn = oauth?.connection;
  const isConnected = !!oauth?.connected;
  const oauthReady = !!oauth?.oauth_available;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-settings-page">
      <div><h1 className="font-display text-3xl font-bold tracking-tight">Store settings</h1><p className="text-slate-500">Your public storefront details</p></div>

      {/* Support · WhatsApp */}
      {support?.available && (
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-emerald-50/30 to-white max-w-2xl" data-testid="support-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-display flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-emerald-600" />
              Need help? Talk to our support team
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">
              Reach us on WhatsApp — we'll get back within minutes on working hours. Your store details are attached automatically so we can locate your account without asking again.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <a href={support.wa_link} target="_blank" rel="noreferrer" className="flex-1" data-testid="contact-support-link">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 h-11 font-semibold">
                  <MessageCircle className="w-4 h-4 mr-2" />Contact Support on WhatsApp
                </Button>
              </a>
              <Button
                type="button"
                variant="outline"
                className="h-11 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => { navigator.clipboard.writeText(support.prefilled_text || ""); toast.success("Message copied — paste into WhatsApp"); }}
                data-testid="support-copy-btn"
              >
                <Copy className="w-4 h-4 mr-2" />Copy message
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 font-mono flex flex-wrap items-center gap-2">
              Support number: <span className="text-slate-800 font-semibold">+{support.whatsapp_number}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 max-w-2xl">
        <CardHeader><CardTitle className="font-display">Store information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 pb-3 border-b border-slate-100">
            <div className="w-20 h-20 rounded-xl border border-slate-200 overflow-hidden bg-slate-50 grid place-items-center flex-shrink-0" data-testid="logo-preview">
              {s.logo_url ? <img src={s.logo_url} alt="logo" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <Label>Logo</Label>
              <input ref={logoInputRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
              <div className="flex gap-2 mt-1.5">
                <Button size="sm" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="logo-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" />{uploadingLogo ? "Uploading…" : s.logo_url ? "Replace" : "Upload logo"}
                </Button>
                {s.logo_url && <Button size="sm" variant="ghost" className="text-red-600" onClick={removeLogo} data-testid="logo-remove-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove</Button>}
              </div>
              <div className="text-xs text-slate-400 mt-1.5">PNG / JPG / WEBP · auto-resized to 512px · max 5MB</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Store name</Label><Input value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="settings-name" /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
            <div><Label>City</Label><Input value={s.city || ""} onChange={(e) => setS({ ...s, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={s.state || ""} onChange={(e) => setS({ ...s, state: e.target.value })} /></div>
            <div><Label>Pincode</Label><Input value={s.pincode || ""} onChange={(e) => setS({ ...s, pincode: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={s.mobile || ""} onChange={(e) => setS({ ...s, mobile: e.target.value })} /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 max-w-2xl" data-testid="auto-purge-card">
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Trash2 className="w-4 h-4 text-slate-600" />Auto-purge uploads</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Automatically delete customer files after print</div>
              <div className="text-xs text-slate-500 mt-1">A privacy-friendly default. Once ON, files from completed jobs are permanently removed from Print Plus storage after the configured number of days. Job records (for accounting) are kept.</div>
            </div>
            <Switch checked={(s.auto_purge_days || 0) > 0} onCheckedChange={(v) => setS({ ...s, auto_purge_days: v ? (s.auto_purge_days || 7) : 0 })} data-testid="settings-auto-purge-toggle" />
          </div>
          {(s.auto_purge_days || 0) > 0 && (
            <div className="flex items-end gap-3 pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs">Delete after</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number" min={1} max={90}
                    className="h-9 w-24 font-mono"
                    value={s.auto_purge_days || 7}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(90, parseInt(e.target.value || "0", 10) || 0));
                      setS({ ...s, auto_purge_days: n });
                    }}
                    data-testid="settings-auto-purge-days"
                  />
                  <span className="text-sm text-slate-600">days</span>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 pb-2">Runs daily at 2am IST · valid range 1–90 days</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 max-w-2xl">
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orange-500" />Job approval</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Review before print</div>
              <div className="text-xs text-slate-500 mt-1">When ON, every paid job waits in <span className="font-mono">pending_approval</span> until you review it in the preview dialog. Turn OFF for fully automatic printing.</div>
            </div>
            <Switch checked={!!s.require_approval} onCheckedChange={(v) => setS({ ...s, require_approval: v })} data-testid="settings-require-approval" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 max-w-2xl" data-testid="cash-settings-card">
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Wallet className="w-4 h-4 text-orange-500" />Payment methods</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Accept cash at counter <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded ml-1">Default</span></div>
              <div className="text-xs text-slate-500 mt-1">Adds a "Pay at counter" option to your public print page. Cash orders always wait in <span className="font-mono">pending_approval</span> — the job only prints after you approve it in the queue (once the customer hands over the cash).</div>
            </div>
            <Switch checked={!!s.accept_cash} onCheckedChange={(v) => setS({ ...s, accept_cash: v })} data-testid="settings-accept-cash" />
          </div>
          <div className="flex items-start justify-between gap-4 pt-3 border-t border-slate-100">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Accept online payments <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded ml-1">Requires live Razorpay</span></div>
              <div className="text-xs text-slate-500 mt-1">Show a "Pay online" option (UPI / cards / netbanking) on the public page. Money settles into <b>your</b> bank — 100% of it. You must connect Razorpay or add live Key ID + Secret below before you can flip this on.</div>
              {!s.accept_online && !oauth?.connected && !(pay?.has_custom && pay?.active_mode === "live") && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 inline-block">
                  🔒 No live Razorpay credentials found — connect below to unlock this toggle.
                </div>
              )}
            </div>
            <Switch
              checked={!!s.accept_online}
              disabled={!s.accept_online && !oauth?.connected && !(pay?.has_custom && pay?.active_mode === "live")}
              onCheckedChange={(v) => setS({ ...s, accept_online: v })}
              data-testid="settings-accept-online"
            />
          </div>
          {!s.accept_cash && !s.accept_online && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="no-payment-warning">
              ⚠ Your shop currently accepts <b>no</b> payment methods — customers won't be able to place any orders. Enable at least one above.
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-settings">Save all changes</Button>

      {/* =================== RAZORPAY OAUTH CONNECT (PRIMARY) =================== */}
      <Card className="border-slate-200 max-w-2xl" data-testid="razorpay-connect-card">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Link2 className="w-4 h-4 text-orange-500" />Payments — connect your Razorpay account
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            When a customer pays online for a print job, <b>100% of the amount lands directly in your own Razorpay account</b> (minus Razorpay's transaction fee). We never touch your money. Just click below, sign in on Razorpay's page, and approve access.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Connected state */}
          {isConnected && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="razorpay-connected-state">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-emerald-900">Razorpay connected</div>
                  <div className="mt-2 space-y-1 text-xs text-emerald-800">
                    {conn?.business_name && <div><span className="text-emerald-700/70">Business:</span> <span className="font-medium">{conn.business_name}</span></div>}
                    {conn?.razorpay_account_id && <div><span className="text-emerald-700/70">Account:</span> <span className="font-mono">{conn.razorpay_account_id}</span></div>}
                    {conn?.contact_email && <div><span className="text-emerald-700/70">Email:</span> {conn.contact_email}</div>}
                    <div className="flex items-center gap-2 pt-1">
                      <Badge variant="outline" className="text-[10px] bg-white border-emerald-300 text-emerald-800">{(conn?.mode || "test").toUpperCase()}</Badge>
                      <span className="text-emerald-700/70">Connected {conn?.connected_at ? new Date(conn.connected_at).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-emerald-100">
                <Button size="sm" variant="outline" onClick={connectRazorpay} disabled={connecting} data-testid="razorpay-reconnect-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reconnect
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={disconnectRazorpay} data-testid="razorpay-disconnect-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />Disconnect
                </Button>
              </div>
            </div>
          )}

          {/* Not connected state */}
          {!isConnected && (
            <>
              {oauthReady ? (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-sm text-slate-700">
                    Click <b>Connect Razorpay</b> to be redirected to Razorpay's login page. Sign in with your existing Razorpay account, approve access, and you'll come right back here.
                  </div>
                  <Button
                    onClick={connectRazorpay}
                    disabled={connecting}
                    className="mt-3 bg-[#3395FF] hover:bg-[#1e7bd6] text-white"
                    data-testid="razorpay-connect-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    {connecting ? "Redirecting to Razorpay…" : "Connect Razorpay"}
                  </Button>
                  <div className="text-[11px] text-slate-400 mt-2">Mode: <span className="font-mono">{oauth?.oauth_mode?.toUpperCase() || "TEST"}</span> · You'll authorise access on razorpay.com — we never see your password.</div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="razorpay-not-configured">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900">
                      <b>Razorpay OAuth is not configured on the platform yet.</b> The platform admin still needs to complete Razorpay Partner enrollment and set the OAuth client credentials. Until then, customer payments will use the platform's default keys.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Partner help panel */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/40">
            <button
              type="button"
              onClick={() => setShowPartnerHelp(!showPartnerHelp)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
              data-testid="partner-help-toggle"
            >
              <span className="text-xs font-semibold text-slate-700">How does this work? &amp; how to become a Razorpay Partner</span>
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showPartnerHelp ? "rotate-180" : ""}`} />
            </button>
            {showPartnerHelp && (
              <div className="px-4 pb-4 pt-1 text-xs text-slate-600 space-y-3 border-t border-slate-200">
                <div>
                  <div className="font-semibold text-slate-800 mb-1">What happens when I click "Connect Razorpay"?</div>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>You're redirected to Razorpay's secure login page.</li>
                    <li>You sign in with your <b>existing</b> Razorpay account (no new signup).</li>
                    <li>You approve this app's access to create orders on your behalf.</li>
                    <li>Razorpay redirects back and marks your account as connected.</li>
                    <li>From that moment, every online payment flows: <b>customer → Razorpay → your bank account</b>. We never touch the funds.</li>
                  </ol>
                </div>
                <div>
                  <div className="font-semibold text-slate-800 mb-1">I don't have a Razorpay account yet</div>
                  <div>
                    Sign up free at <a href="https://razorpay.com" target="_blank" rel="noreferrer" className="text-orange-600 underline">razorpay.com</a>, complete KYC (PAN, bank, address proof — ~1 day), then come back and click Connect.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-slate-800 mb-1">Platform admin — Razorpay Partner setup (one-time)</div>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Apply as a Technology Partner at <a href="https://razorpay.com/partners/" target="_blank" rel="noreferrer" className="text-orange-600 underline">razorpay.com/partners</a>.</li>
                    <li>Once approved, go to Partner Dashboard → <b>Partners → Applications → Create Application</b>.</li>
                    <li>
                      Whitelist this exact callback URL:
                      {oauth?.redirect_uri && (
                        <div className="mt-1 flex items-center gap-2">
                          <code className="flex-1 bg-slate-100 px-2 py-1 rounded text-[10.5px] font-mono break-all">{oauth.redirect_uri}</code>
                          <button onClick={copyRedirectUri} className="text-slate-500 hover:text-slate-800" data-testid="copy-callback-btn"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </li>
                    <li>Download <code>client_id</code> + <code>client_secret</code> (test &amp; live).</li>
                    <li>Add them to the server as <code>RAZORPAY_OAUTH_CLIENT_ID</code> and <code>RAZORPAY_OAUTH_CLIENT_SECRET</code>.</li>
                  </ol>
                </div>
                <a href="https://razorpay.com/docs/partners/technology-partners/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-600 hover:underline">
                  Razorpay Partner docs <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* =================== ADVANCED: MANUAL KEYS (COLLAPSED) =================== */}
      <div className="max-w-2xl">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          data-testid="advanced-toggle"
        >
          <div className="flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-semibold text-slate-600">Advanced — use Razorpay API keys directly (fallback)</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>
      </div>

      {showAdvanced && (
        <Card className="border-slate-200 max-w-2xl" data-testid="payment-gateway-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-orange-500" />Manual key entry (advanced)
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              For shops that haven't OAuth-connected yet. Not recommended — use the Connect Razorpay button above instead. Keys are Fernet-encrypted at rest.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5" data-testid="payment-current-state">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Currently active (fallback)</div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className={`text-[10px] ${pay?.source === "store" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {pay?.source === "store" ? "Your account" : pay?.source === "platform" ? "Platform default" : "Not configured"}
                  </Badge>
                  {pay?.active_mode && <Badge variant="outline" className={`text-[10px] ${pay?.active_mode === "live" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{(pay.active_mode || "").toUpperCase()}</Badge>}
                </div>
              </div>
              <div className="mt-2 font-mono text-sm text-slate-800 break-all" data-testid="payment-active-key">
                {pay?.active_key_id_masked || <span className="text-red-600 text-xs">No Razorpay keys configured</span>}
              </div>
              {pay?.has_custom && (
                <div className="text-[11px] text-slate-500 mt-1.5">
                  Last updated {pay.updated_at ? new Date(pay.updated_at).toLocaleString() : "—"}
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Mode</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Button type="button" size="sm" variant={payDraft.mode === "test" ? "default" : "outline"} className={payDraft.mode === "test" ? "bg-slate-800 hover:bg-slate-900" : ""} onClick={() => setPayDraft({ ...payDraft, mode: "test" })} data-testid="payment-mode-test">Test</Button>
                  <Button type="button" size="sm" variant={payDraft.mode === "live" ? "default" : "outline"} className={payDraft.mode === "live" ? "bg-orange-500 hover:bg-orange-600" : ""} onClick={() => setPayDraft({ ...payDraft, mode: "live" })} data-testid="payment-mode-live">Live</Button>
                  <span className="text-[11px] text-slate-500 ml-1">{payDraft.mode === "live" ? "Real money · needs rzp_live_ keys" : "Sandbox · needs rzp_test_ keys"}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs">Key ID</Label>
                <Input placeholder={payDraft.mode === "live" ? "rzp_live_XXXXXXXXXXXX" : "rzp_test_XXXXXXXXXXXX"} value={payDraft.key_id} onChange={(e) => setPayDraft({ ...payDraft, key_id: e.target.value.trim() })} className="font-mono text-sm" data-testid="payment-key-id-input" />
              </div>
              <div>
                <Label className="text-xs">Key Secret</Label>
                <div className="relative">
                  <Input type={showSecret ? "text" : "password"} placeholder="••••••••••••••••••••••••" value={payDraft.key_secret} onChange={(e) => setPayDraft({ ...payDraft, key_secret: e.target.value })} className="font-mono text-sm pr-10" data-testid="payment-key-secret-input" />
                  <button type="button" onClick={() => setShowSecret((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" data-testid="payment-secret-toggle">
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Encrypted at rest. Never displayed once saved.</div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" size="sm" variant="outline" onClick={testPay} disabled={testing} data-testid="payment-test-btn"><PlugZap className="w-3.5 h-3.5 mr-1.5" />{testing ? "Testing…" : "Test connection"}</Button>
                <Button type="button" size="sm" onClick={savePay} disabled={savingPay || !payDraft.key_id || !payDraft.key_secret} className="bg-orange-500 hover:bg-orange-600" data-testid="payment-save-btn"><KeyRound className="w-3.5 h-3.5 mr-1.5" />{savingPay ? "Saving…" : "Save & verify"}</Button>
                {pay?.has_custom && <Button type="button" size="sm" variant="ghost" onClick={resetPay} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="payment-reset-btn"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset to platform default</Button>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
